import { useState, useCallback } from 'react'

import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DescriptionIcon from '@mui/icons-material/Description'
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Typography,
  Alert,
} from '@mui/material'
import mammoth from 'mammoth/mammoth.browser'
import { toast } from 'react-toastify'

import { UploadLegalDocumentFile } from 'services/RestClient'

import { ACCEPTED_FILE_TYPES } from './constants'
import { useSanitizeDocxHtml } from './hooks/useSanitizeDocxHtml'

interface DocxUploadProps {
  onDocxParsed: (content: string, fileUri: string, fileName: string) => void
  onFileNameResolved?: (fileName: string) => void
}

export const DocxUpload = ({ onDocxParsed, onFileNameResolved }: DocxUploadProps) => {
  const [isDragOver, setIsDragOver] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)

  const sanitize = useSanitizeDocxHtml()

  const validateFile = (file: File): boolean => {
    setError(null)

    const extension = file.name.split('.').pop()?.toLowerCase()
    if (extension !== 'docx') {
      setError('Only .docx files are accepted.')
      return false
    }

    const validMimeTypes = [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ]
    if (!validMimeTypes.includes(file.type) && file.type !== '') {
      setError('Invalid file type. Please upload a .docx file.')
      return false
    }

    const maxSize = 10 * 1024 * 1024
    if (file.size > maxSize) {
      setError('File is too large. Maximum size is 10MB.')
      return false
    }

    return true
  }

  const processDocxFile = useCallback(
    async (file: File) => {
      if (!validateFile(file)) return

      setIsProcessing(true)
      setSelectedFile(file)
      setError(null)

      try {
        const arrayBuffer = await file.arrayBuffer()

        const htmlResult = await mammoth.convertToHtml({
          arrayBuffer,
          styleMap: [
            "p[style-name='Heading 1'] => h1:fresh",
            "p[style-name='Heading 2'] => h2:fresh",
            "p[style-name='Heading 3'] => h3:fresh",
          ],
        })
        const formattedContent = sanitize(htmlResult.value)

        const originalFileName = file.name
        const nameWithoutExtension = originalFileName.replace(/\.docx$/i, '')

        if (onFileNameResolved) {
          onFileNameResolved(nameWithoutExtension)
        }

        const formData = new FormData()
        formData.append('file', file)

        let fileUri = ''

        try {
          const uploadResponse = await UploadLegalDocumentFile(formData)
          fileUri = uploadResponse.data.absoluteUri
        } catch (uploadError) {
          console.error('File upload failed:', uploadError)
          toast.warning(
            'File upload failed, but content was parsed successfully. The original file will not be stored.',
          )
        }

        onDocxParsed(formattedContent, fileUri, originalFileName)
        toast.success('DOCX file parsed successfully!')
      } catch (err: any) {
        console.error('DOCX parsing error:', err)
        setError(`Failed to parse DOCX file: ${err.message || 'Unknown error'}`)
        toast.error('Failed to parse DOCX file')
      } finally {
        setIsProcessing(false)
      }
    },
    [onDocxParsed, onFileNameResolved, sanitize],
  )

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      processDocxFile(file)
    }
    event.target.value = ''
  }

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      setIsDragOver(false)
      const file = event.dataTransfer.files?.[0]
      if (file) {
        processDocxFile(file)
      }
    },
    [processDocxFile],
  )

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = () => {
    setIsDragOver(false)
  }

  return (
    <Box>
      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        sx={{
          p: 4,
          border: '2px dashed',
          borderColor: isDragOver ? 'primary.main' : error ? 'error.main' : 'grey.400',
          borderRadius: 2,
          textAlign: 'center',
          bgcolor: isDragOver ? 'action.hover' : 'background.paper',
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'action.hover',
          },
        }}>
        {isProcessing ? (
          <Box>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant='body1'>
              Processing {selectedFile?.name}...
            </Typography>
            <LinearProgress sx={{ mt: 2, maxWidth: 300, mx: 'auto' }} />
          </Box>
        ) : (
          <Box>
            <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant='body1' gutterBottom>
              Drag & drop a DOCX file here, or click to browse
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Only .docx files are accepted (max 10MB).
            </Typography>
            <Button
              variant='outlined'
              component='label'
              startIcon={<DescriptionIcon />}
              sx={{ mt: 2 }}>
              Select DOCX File
              <input
                type='file'
                accept={ACCEPTED_FILE_TYPES}
                hidden
                onChange={handleFileSelect}
              />
            </Button>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity='error' sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}

      {selectedFile && !isProcessing && !error && (
        <Alert severity='success' sx={{ mt: 1 }} icon={<DescriptionIcon />}>
          Parsed: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
        </Alert>
      )}
    </Box>
  )
}
